/**
 * Umowy i dokumenty (spec 03 §2).
 *
 * Lista pochodzi z akt: dopóki dział HR nie wystawi dokumentu, portal pokazuje
 * stan pusty zamiast czterech pozycji z makiety [D19]. Podpisu przez mObywatel
 * nie ma (ADR-0016 §3, D27): „Podpisz" jest wyłączony z widoczną plakietką
 * WKRÓTCE i notą, a dokument do podpisu da się pobrać i przeczytać (D96).
 */
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Badge, Button, Card, Document, EmptyState, Spinner, StatusPill, useToast } from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import { employeeSigns } from '@k1p4/shared'
import { ApiError, api, type PortalDocumentItem } from '../../api/client'
import styles from './portal.module.css'

const STATUS_TONE: Record<PortalDocumentItem['status'], 'success' | 'pink' | 'neutral'> = {
  szkic: 'neutral',
  do_akceptacji: 'neutral',
  do_podpisu: 'pink',
  podpisany: 'success',
  zarchiwizowany: 'neutral',
}

export function PortalDocuments() {
  const { t } = useTranslation()
  const toast = useToast()
  const documents = useQuery({ queryKey: ['me', 'documents'], queryFn: () => api.meDocuments() })
  /* Plik może powstawać dopiero teraz z zapisanej treści — błąd to komunikat, nie JSON (grupa 48). */
  const download = useMutation({
    mutationFn: (item: PortalDocumentItem) =>
      api.downloadMyDocumentFile(item.id, `${item.title}.pdf`),
    onError: (caught) => {
      const code = caught instanceof ApiError ? caught.code : null
      toast.show(
        t(
          code === 'PDF_UNAVAILABLE'
            ? 'portal.documents.pdfUnavailable'
            : code === 'FILE_MISSING'
              ? 'portal.documents.noContent'
              : 'portal.documents.downloadFailed',
        ),
        { tone: 'danger' },
      )
    },
  })

  if (documents.isPending) return <Spinner label={t('common.loading')} />
  if (documents.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const items = documents.data.items

  return (
    <>
      <p className={styles.lead}>{t('portal.documents.lead')}</p>

      {items.length === 0 ? (
        <EmptyState
          icon={<Document size={28} />}
          title={t('portal.documents.empty')}
          description={t('portal.documents.emptyBody')}
        />
      ) : (
        <Card pad="flush" className={styles.list}>
          {items.map((item) => {
            /* „Podpisz" tylko tam, gdzie podpis pracownika jest częścią obiegu (#72). */
            const toSign = item.status === 'do_podpisu' && employeeSigns(item.type)
            /* Plik albo zapisana treść wystarczą — PDF powstaje przy pobraniu (grupa 48). */
            const downloadable = item.fileId !== null || item.hasContent
            const noteId = `sign-note-${item.id}`
            /*
             * Dokument, który wydaje pracodawca (PIT-11, IMIR, karta ewidencji,
             * świadectwo), pracownik ma „Dostępny" i „wystawiony", a nie
             * „Podpisany" — niczego nie podpisywał (spec 03 §2, przegląd Fali 5).
             */
            const available =
              Boolean(item.acknowledgedAt) ||
              (!employeeSigns(item.type) && item.status === 'podpisany')
            return (
              <div key={item.id} className={`${styles.row} ${styles.docRow}`}>
                <span className={styles.rowIcon} aria-hidden="true">
                  <Document size={18} />
                </span>
                <div className={styles.rowBody}>
                  <p className={styles.rowTitle}>{item.title}</p>
                  <p className={styles.rowMeta}>
                    {/* Odpis z procesu zatrudnienia: „zapoznano {data}" (spec 03 §2, grupa 134). */}
                    {item.acknowledgedAt
                      ? t('portal.documents.acknowledged', {
                          date: formatDate(item.date ?? item.acknowledgedAt, 'dot'),
                        })
                      : available && item.date
                      ? t('portal.documents.issued', { date: formatDate(item.date, 'dot') })
                      : item.signedAt
                      ? t('portal.documents.signed', {
                          date: formatDate(item.signedAt, 'dot'),
                        })
                      : item.date
                        ? t('portal.documents.issued', { date: formatDate(item.date, 'dot') })
                        : t('portal.documents.noFile')}
                  </p>
                  {/* Nota widoczna, a nie tylko w `title` — dotyk i klawiatura też ją dostają (D96). */}
                  {toSign ? (
                    <p id={noteId} className={styles.rowNote}>
                      {t('portal.documents.signSoon')}
                    </p>
                  ) : null}
                </div>
                <div className={styles.rowActions}>
                  <StatusPill tone={available ? 'success' : STATUS_TONE[item.status]}>
                    {available
                      ? t('portal.documents.available')
                      : t(`hrDocuments.status.${item.status}`, { defaultValue: item.status })}
                  </StatusPill>
                  {toSign ? (
                    <>
                      <Badge tone="warning">{t('soon.title')}</Badge>
                      <Button size="sm" disabled aria-describedby={noteId}>
                        {t('portal.documents.sign')}
                      </Button>
                    </>
                  ) : null}
                  {/* Dokument do podpisu też da się pobrać i przeczytać przed podpisem (D96). */}
                  {!toSign || downloadable ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!downloadable || download.isPending}
                      {...(downloadable
                        ? { onClick: () => download.mutate(item) }
                        : { title: t('portal.documents.noContent') })}
                    >
                      {t('portal.documents.download')}
                    </Button>
                  ) : null}
                </div>
              </div>
            )
          })}
        </Card>
      )}
    </>
  )
}
